<template>
  <TransitionGroup name="banner-slide" tag="div" class="system-banner-container">
    <div
      v-for="announcement in visibleAnnouncements"
      :key="announcement.id"
      :class="['system-banner', `system-banner--${announcement.severity || 'info'}`]"
    >
      <div class="system-banner__content">
        <font-awesome-icon :icon="severityIcon(announcement.severity)" class="system-banner__icon" />
        <div class="system-banner__text">
          <strong v-if="announcement.title" class="system-banner__title">{{ announcement.title }}</strong>
          <span class="system-banner__message" v-html="renderMarkdown(announcement.message)" />
        </div>
      </div>
      <button class="system-banner__dismiss" @click="dismiss(announcement.id)" title="Dismiss">
        <font-awesome-icon icon="fa-solid fa-xmark" />
      </button>
    </div>
  </TransitionGroup>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { marked } from 'marked'
import { dismissAnnouncementApi } from '@/services/announcement.service'

export default {
  name: 'SystemBanner',
  computed: {
    ...mapGetters(['getVisibleAnnouncements', 'getUserId']),
    visibleAnnouncements() {
      return this.getVisibleAnnouncements
    }
  },
  methods: {
    ...mapMutations(['dismissAnnouncement']),
    dismiss(id) {
      this.dismissAnnouncement(id)
      // Track dismissal server-side
      if (this.getUserId) {
        dismissAnnouncementApi(this.getUserId, id).catch(err =>
          console.warn('[Announcements] Failed to log dismissal:', err)
        )
      }
    },
    severityIcon(severity) {
      switch (severity) {
        case 'warning': return 'fa-solid fa-triangle-exclamation'
        case 'danger': return 'fa-solid fa-circle-exclamation'
        case 'maintenance': return 'fa-solid fa-wrench'
        default: return 'fa-solid fa-circle-info'
      }
    },
    renderMarkdown(text) {
      if (!text) return ''
      return marked.parseInline(text)
    }
  }
}
</script>

<style scoped>
.system-banner-container {
  position: fixed;
  width: 100vw;
  top: 0;
  left: 0;
  z-index: 100;
}

.system-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  min-height: 2.5rem;
}

.system-banner__content {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex: 1;
  min-width: 0;
}

.system-banner__icon {
  flex-shrink: 0;
  font-size: 0.875rem;
}

.system-banner__text {
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  flex-wrap: wrap;
  min-width: 0;
}

.system-banner__title {
  font-weight: 700;
  white-space: nowrap;
}

.system-banner__message {
  opacity: 0.9;
}
.system-banner__message :deep(a) {
  color: inherit;
  text-decoration: underline;
  font-weight: 600;
}
.system-banner__message :deep(code) {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.1em 0.35em;
  border-radius: 0.25em;
  font-size: 0.85em;
}
.system-banner__message :deep(strong) { font-weight: 700; }
.system-banner__message :deep(p) { margin: 0; display: inline; }

.system-banner__dismiss {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background 0.15s ease;
  margin-left: 0.75rem;
}
.system-banner__dismiss:hover {
  background: rgba(255, 255, 255, 0.35);
}

/* ── Severity Variants ───────────────────── */
.system-banner--info {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}
.system-banner--info .system-banner__dismiss { color: white; }

.system-banner--warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #1c1917;
}
.system-banner--warning .system-banner__dismiss { color: #1c1917; }

.system-banner--danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}
.system-banner--danger .system-banner__dismiss { color: white; }

.system-banner--maintenance {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}
.system-banner--maintenance .system-banner__dismiss { color: white; }

/* ── Transitions ─────────────────────────── */
.banner-slide-enter-active {
  transition: all 0.3s ease-out;
}
.banner-slide-leave-active {
  transition: all 0.2s ease-in;
}
.banner-slide-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}
.banner-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
.banner-slide-move {
  transition: transform 0.3s ease;
}
</style>
